/**
 * 🔒 ShareOwenContextModal — Modal premium de consentement post-booking
 *
 * Opt-in strict. Aucun pré-cochage silencieux (Scope B juste visuellement
 * marqué "Recommandé", nécessite clic explicite). Respect RGPD total.
 */
import React, { useState } from 'react';
import { X, Shield, CheckCircle, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';

const T = {
  bg: '#FBF9F3',
  card: '#FFFFFF',
  ink: '#1C1147',
  text: '#1F2937',
  muted: '#6B7280',
  border: '#E8E2D2',
  gold: '#D4AF37',
  goldDeep: '#B7901F',
  goldLight: '#F3D060',
  ivoryDeep: '#FFFCEC',
  green: '#48825A',
  violetSoft: '#F4EFFF',
  violetAccent: '#7A4FCF',
};

const SCOPES = [
  {
    id: 'A',
    label: 'Synthèse courte uniquement',
    desc: 'Un résumé 3-5 lignes de votre parcours, sans détails spécifiques.',
    tag: null,
  },
  {
    id: 'B',
    label: 'Synthèse + objectifs + critères',
    desc: 'Ce sur quoi vous travaillez et votre avancée sur les critères.',
    tag: 'Recommandé',
  },
  {
    id: 'C',
    label: 'Synthèse + rapports IA',
    desc: 'Inclut vos synthèses post-sessions et points récurrents.',
    tag: null,
  },
  {
    id: 'D',
    label: 'Personnalisation complète',
    desc: 'Choisir chaque catégorie une par une.',
    tag: 'Avancé',
  },
];

const CUSTOM_CATEGORIES = [
  { id: 'context_summary', label: 'Contexte du parcours (module en cours)' },
  { id: 'global_progress', label: 'Progression globale (% validé)' },
  { id: 'objectives', label: 'Objectifs pédagogiques travaillés' },
  { id: 'validated_criteria', label: 'Critères validés' },
  { id: 'non_validated_criteria', label: 'Critères non validés' },
  { id: 'evidences', label: 'Preuves agrégées (sans verbatim)' },
  { id: 'progress_actions', label: 'Actions de progrès 30/60/90 j' },
  { id: 'ai_reports', label: 'Rapports IA synthétiques' },
  { id: 'recurring_difficulties', label: 'Difficultés récurrentes observées' },
];

export default function ShareOwenContextModal({
  bookingId,
  coachName,
  sessionDate,
  token,
  onClose,
  onSuccess,
}) {
  const [selectedScope, setSelectedScope] = useState(null); // pas de pré-coché
  const [customCats, setCustomCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const API = getApiUrl();

  const toggleCat = (id) => {
    setCustomCats((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleAuthorize = async () => {
    if (!selectedScope) {
      toast.error('Sélectionnez un niveau de partage pour continuer.');
      return;
    }
    if (selectedScope === 'D' && customCats.length === 0) {
      toast.error('Sélectionnez au moins une catégorie.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        booking_id: bookingId,
        consent_scope: selectedScope,
      };
      if (selectedScope === 'D') body.custom_categories = customCats;
      const r = await fetch(`${API}/coaching-context/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || `HTTP ${r.status}`);
      toast.success('Partage autorisé · synthèse en cours de génération');
      onSuccess?.(json.share);
      onClose?.();
    } catch (e) {
      toast.error(`Échec : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefuse = () => {
    toast.info("Aucun partage. Votre coach découvrira le contexte en session.");
    onClose?.();
  };

  return (
    <div
      data-testid="share-owen-context-modal"
      className="fixed inset-0 z-[10000] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(12, 8, 32, 0.75)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="max-w-2xl w-full my-4 rounded-2xl overflow-hidden"
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow: '0 24px 60px -12px rgba(28,17,71,0.45)',
        }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-start justify-between"
          style={{
            background: `linear-gradient(135deg, ${T.ink} 0%, #3A2E6E 100%)`,
            color: T.goldLight,
          }}
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.24em] font-semibold">
                Partage de contexte · RGPD
              </span>
            </div>
            <h2
              className="text-xl sm:text-2xl"
              style={{
                color: '#FFF8E0',
                fontFamily: 'Georgia, serif',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              Partager votre contexte Owen avec
              <br />
              <span style={{ color: T.goldLight }}>{coachName || 'votre coach'}</span> ?
            </h2>
            {sessionDate && (
              <p className="text-xs mt-1" style={{ color: 'rgba(243,208,96,0.7)' }}>
                Session du {sessionDate}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="share-owen-context-close"
            className="text-white/70 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm" style={{ color: T.text }}>
            Votre coach humain peut vous accompagner encore mieux si vous partagez
            votre contexte de parcours avec Owen. <b>C'est vous qui décidez</b> ce
            qui est partagé, et pour combien de temps.
          </p>

          {/* Scope selection */}
          <div className="space-y-2" data-testid="share-owen-context-scopes">
            {SCOPES.map((s) => (
              <label
                key={s.id}
                className="flex items-start gap-3 p-3 rounded-xl cursor-pointer transition"
                style={{
                  background: selectedScope === s.id ? T.ivoryDeep : T.card,
                  border: `1px solid ${
                    selectedScope === s.id ? T.gold : T.border
                  }`,
                  boxShadow:
                    selectedScope === s.id
                      ? `0 4px 16px -8px ${T.gold}66`
                      : 'none',
                }}
              >
                <input
                  type="radio"
                  name="scope"
                  checked={selectedScope === s.id}
                  onChange={() => setSelectedScope(s.id)}
                  data-testid={`share-owen-context-scope-${s.id}`}
                  className="mt-1 accent-[#D4AF37]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-sm font-semibold"
                      style={{ color: T.ink }}
                    >
                      {s.id} — {s.label}
                    </span>
                    {s.tag && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                        style={{
                          background: T.gold,
                          color: T.ink,
                          letterSpacing: '0.05em',
                        }}
                      >
                        {s.tag}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: T.muted }}>
                    {s.desc}
                  </p>
                </div>
              </label>
            ))}
          </div>

          {/* Custom categories if D */}
          {selectedScope === 'D' && (
            <div
              className="p-4 rounded-xl"
              style={{
                background: T.violetSoft,
                border: `1px solid ${T.violetAccent}33`,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Settings2 className="w-4 h-4" style={{ color: T.violetAccent }} />
                <span
                  className="text-[11px] uppercase tracking-[0.2em] font-semibold"
                  style={{ color: T.violetAccent }}
                >
                  Personnalisation
                </span>
              </div>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                data-testid="share-owen-context-custom-categories"
              >
                {CUSTOM_CATEGORIES.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-start gap-2 p-2 rounded-lg cursor-pointer text-xs"
                    style={{
                      background: customCats.includes(c.id)
                        ? T.card
                        : 'transparent',
                      color: T.text,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={customCats.includes(c.id)}
                      onChange={() => toggleCat(c.id)}
                      className="mt-0.5 accent-[#7A4FCF]"
                      data-testid={`share-owen-cat-${c.id}`}
                    />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Excluded info */}
          <div
            className="p-3 rounded-xl text-xs"
            style={{
              background: '#F5EEDD',
              border: `1px solid ${T.border}`,
              color: T.text,
            }}
          >
            <p className="font-semibold mb-1" style={{ color: T.goldDeep }}>
              Ce qui est TOUJOURS EXCLU :
            </p>
            <ul className="space-y-0.5 pl-1" style={{ color: T.muted }}>
              <li>✗ Verbatim complet des conversations Owen</li>
              <li>✗ Contenu émotionnel brut</li>
              <li>✗ Informations personnelles libres</li>
              <li>✗ Données RH internes</li>
            </ul>
          </div>

          <div className="flex items-center gap-3 text-xs" style={{ color: T.muted }}>
            <CheckCircle className="w-4 h-4" style={{ color: T.green }} />
            <span>
              Durée : <b>14 jours</b> après la session · Vous pouvez révoquer à tout moment
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleRefuse}
              disabled={loading}
              data-testid="share-owen-context-refuse-btn"
              className="flex-1 py-3 rounded-full text-sm font-semibold transition"
              style={{
                background: T.card,
                color: T.text,
                border: `1px solid ${T.border}`,
              }}
            >
              Refuser
            </button>
            <button
              onClick={handleAuthorize}
              disabled={loading || !selectedScope}
              data-testid="share-owen-context-authorize-btn"
              className="flex-1 py-3 rounded-full text-sm font-bold transition disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                color: T.ink,
                boxShadow: `0 8px 22px -8px ${T.gold}AA`,
              }}
            >
              {loading ? 'Envoi…' : 'Autoriser le partage'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
